import { PercentPipe } from '@angular/common';
import { Component, computed, effect, inject, input, untracked } from '@angular/core';
import { UiNumberComponent, UiPopoverComponent } from '@design-system';
import { OrderFormProducerService } from '../../events/order-form/order-form-producer.service';
import { calculateCumulativeDepthQuantity } from '../order-form/tools/order-calculator.tools';
import { IDepthData } from './models/market-depth.model';

@Component({
	selector: 'market-depth-best-limit',
	// The ladder's figures go through `ui-number` rather than the `number` pipe so
	// «نمایش اعداد فارسی» reaches them — see `UI_NUMBER_DIGIT_TRANSFORM`.
	imports: [PercentPipe, UiNumberComponent, UiPopoverComponent],
	templateUrl: './market-depth-best-limit.component.html'
})
export class MarketDepthBestLimitComponent {
	private readonly orderFormProducer = inject(OrderFormProducerService);

	/** When true, price/quantity clicks push values into the order-form event bus. */
	readonly syncOrderForm = input(false);

	readonly depthValues = input<IDepthData[]>([]);
	readonly previousClosePrice = input(0);

	/**
	 * Render model: pads the ladder to 3 levels and coerces missing figures to 0, so an
	 * empty or short book shows zeroed rows rather than nothing. Order-form publishing still
	 * reads the raw `depthValues()`, so padding never leaks into the form.
	 */
	readonly rows = computed<Required<IDepthData>[]>(() => {
		const values = this.depthValues();

		return Array.from({ length: 3 }, (_, index) => ({
			bestBuyLimitPrice: values[index]?.bestBuyLimitPrice ?? 0,
			bestBuyLimitQuantity: values[index]?.bestBuyLimitQuantity ?? 0,
			bestSellLimitPrice: values[index]?.bestSellLimitPrice ?? 0,
			bestSellLimitQuantity: values[index]?.bestSellLimitQuantity ?? 0
		}));
	});

	maxQuantity = computed(() =>
		Math.max(...this.rows().map(a => a.bestBuyLimitQuantity), ...this.rows().map(a => a.bestSellLimitQuantity))
	);

	constructor() {
		effect(() => {
			const values = this.depthValues();
			untracked(() => this.publishBestLimits(values));
		});
	}

	onPriceClick(price: number | undefined): void {
		if (!this.syncOrderForm() || !price) {
			return;
		}

		this.orderFormProducer.updateOrderForm({ price });
	}

	onBuyQuantityClick(depthIndex: number, price: number | undefined): void {
		if (!this.syncOrderForm() || !price) {
			return;
		}

		const quantity = calculateCumulativeDepthQuantity(
			this.depthValues().map(row => row.bestBuyLimitQuantity),
			depthIndex
		);
		this.orderFormProducer.updateOrderForm({ price, quantity });
	}

	onSellQuantityClick(depthIndex: number, price: number | undefined): void {
		if (!this.syncOrderForm() || !price) {
			return;
		}

		const quantity = calculateCumulativeDepthQuantity(
			this.depthValues().map(row => row.bestSellLimitQuantity),
			depthIndex
		);
		this.orderFormProducer.updateOrderForm({ price, quantity });
	}

	private publishBestLimits(rows: IDepthData[]): void {
		if (!this.syncOrderForm() || !rows.length) {
			return;
		}

		this.orderFormProducer.updateOrderForm({
			bestBuyLimitPrice: rows[0].bestBuyLimitPrice,
			bestSellLimitPrice: rows[0].bestSellLimitPrice
		});
	}
}
